import { Button, Flex, Spinner, Text } from '@chakra-ui/react'
import { useState } from 'react'

import { FileInput } from '../../../components/FileInput'
import { getApiErrorMessage } from '../../../helpers'
import { AdmissionService } from '../../../services/Admission/AdmissionService'
import { IAdmissionDocumentRaw, IAdmissionVacancyDocumentRaw } from '../../../services/Admission/types'

interface IAdmissionDocumentFieldProps {
  token: string
  requirement: IAdmissionVacancyDocumentRaw
  /** The file already kept in the draft for this document. */
  uploaded?: IAdmissionDocumentRaw
  onUploaded: (document: IAdmissionDocumentRaw) => void
  onRemoved: (documentId: number) => void
  /** Errors from the submission (`documents.{id}`). */
  error?: string[]
  isDisabled?: boolean
}

const service = new AdmissionService()

/**
 * One document the role asks for. The file goes to the draft as soon as it is picked, so it is
 * kept when the person saves for later; a failed upload stays on screen with "Tentar novamente".
 */
export default function AdmissionDocumentField({
  token,
  requirement,
  uploaded,
  onUploaded,
  onRemoved,
  error,
  isDisabled
}: IAdmissionDocumentFieldProps) {
  const [pending, setPending] = useState<File | null>(null)
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const upload = async (file: File) => {
    setPending(file)
    setUploadError(null)
    setBusy('upload')
    try {
      onUploaded(await service.uploadDocument(token, requirement.id, file))
      setPending(null)
    } catch (response) {
      setUploadError(getApiErrorMessage(response) ?? 'Não foi possível enviar o arquivo.')
    } finally {
      setBusy(null)
    }
  }

  // Dropping the failed file also drops its error: what is saved is the previous one, if any.
  const clearPending = () => {
    setPending(null)
    setUploadError(null)
  }

  const remove = async () => {
    setBusy('remove')
    setUploadError(null)
    try {
      await service.deleteDocument(token, requirement.id)
      onRemoved(requirement.id)
    } catch (response) {
      setUploadError(getApiErrorMessage(response) ?? 'Não foi possível remover o arquivo.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <FileInput
      accept="admissionDocument"
      maxSizeMB={10}
      label={requirement.name}
      isRequired={requirement.required}
      value={pending}
      onChange={(file) => (file ? upload(file) : clearPending())}
      existingUrl={uploaded?.url}
      error={uploadError ?? error}
      isDisabled={isDisabled || busy !== null}
      helperText={
        busy === 'upload' ? (
          <Flex align="center" gap={2}>
            <Spinner size="xs" />
            Enviando arquivo...
          </Flex>
        ) : pending && uploadError ? (
          // A failed upload doesn't touch what is saved, so the previous file is still named here.
          <Flex align="center" gap={2} wrap="wrap">
            <Button size="xs" variant="link" colorScheme="magenta" onClick={() => upload(pending)}>
              Tentar novamente
            </Button>
            {uploaded && <Text as="span">Continua salvo: {uploaded.original_name}</Text>}
          </Flex>
        ) : uploaded && !pending ? (
          <Flex align="center" gap={2} wrap="wrap">
            <Text as="span">Enviado: {uploaded.original_name}</Text>
            <Button size="xs" variant="link" colorScheme="red" onClick={remove} isLoading={busy === 'remove'}>
              Remover
            </Button>
          </Flex>
        ) : undefined
      }
    />
  )
}
